import React, { useEffect, useMemo, useState } from "react";
import { AutoSizer, Column, SortDirection, Table } from "react-virtualized";
import { format, parseISO } from "date-fns";
import { getNumericComparator, getStringComparator, reverseOrder } from "@/utils/collect";
import styles from "@/components/LanguageModelArtifact/style.module.css";
import { getModelIcon } from "@/containers/ChatGPT/reducer";
import ModelStatus from "@/components/LanguageModelArtifact/Models/ModelStatus";
import { Button } from "@/aible";
import EvaluatorStatus from "@/components/LanguageModelArtifact/Models/EvaluatorStatus";
import { FineTuningExecutionType } from "@/types/content_generation";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/aible/DropdownMenu/DropdownMenu";
import { useDispatch, useSelector } from "@/types/store";
import * as accountsReducer from "@/containers/Accounts/reducer";
import * as aiServiceReducer from "@/containers/AiService/reducer";
import * as fineTuningReducer from "@/containers/FineTuning/reducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { FEEDBACK_BREAKPOINTS } from "@/containers/LanguageModelArtifact/reducer";
const ModelTypeIcon = ({ model_id, source_type, name, source }) => {
    const icon = useSelector((state) => getModelIcon(state, model_id, source_type, name, source));
    return React.createElement("img", { className: styles.modelTableLogo, src: icon });
};
const KEY_CLASSES = {
    name: styles.fineTuningItem,
    evaluatorStatus: styles.fineTuningItem,
    source: styles.fineTuningSourceItem,
    status: styles.fineTuningItem,
    owner: undefined,
    createdDate: undefined,
    actions: styles.fineTuningSettings,
    more: styles.fineTuningSettings
};
const KEY_SET = new Set(Object.keys(KEY_CLASSES));
function isValidDataKey(x) {
    return KEY_SET.has(x);
}
const COLUMNS = [
    { label: "Name", dataKey: "name", width: 580, flexGrow: 1 },
    { label: "Model Evaluation", dataKey: "evaluatorStatus", width: 220 },
    { label: "Source", dataKey: "source", width: 280 },
    { label: "Status", dataKey: "status", width: 140 },
    { label: "Added By", dataKey: "owner", width: 200 },
    { label: "Date Created", dataKey: "createdDate", width: 160 },
    { label: "", dataKey: "actions", width: 280 },
    { label: "", dataKey: "more", width: 40 }
];
const ProvisionedModelsTable = ({ type, searchFilter, handleModelEvaluationAction, handleFineTuneAction, handleEvaluatorAction }) => {
    const dispatch = useDispatch();
    const [isLoading, updateIsLoading] = useState(false);
    const [sortBy, updateSortBy] = useState("createdDate");
    const [sortDirection, updateSortDirection] = useState(SortDirection.DESC);
    const aiServiceModels = useSelector((state) => aiServiceReducer.getModels(state, type));
    const aiServiceConnections = useSelector((state) => aiServiceReducer.getConnections(state));
    const fineTuningExecutionsRaw = useSelector((state) => fineTuningReducer.getFineTunings(state));
    const defaultSortedData = useMemo(() => {
        return Object.values(aiServiceModels)
            ?.map((model) => {
            const createdDate = parseISO(model.created_date + "Z");
            const formattedDate = format(createdDate, "MM/dd/yyyy, hh:mm aa");
            const connectionId = model.connection_id;
            const connection = aiServiceConnections[connectionId ?? -1];
            const relatedEvaluators = type != "CONTENT_GENERATION"
                ? []
                : Object.values(fineTuningExecutionsRaw)
                    .filter((evaluator) => evaluator.type == FineTuningExecutionType.EVALUATION &&
                    evaluator.base_model_version_id == model.version_id)
                    .toSorted(reverseOrder(getStringComparator((x) => x.created_date)));
            let relatedEvaluatorStatus = "notEvaluated";
            let relatedEvaluatorId;
            if (relatedEvaluators.length > 0) {
                if (relatedEvaluators[0].status === "PENDING") {
                    relatedEvaluatorStatus = "pending";
                }
                if (relatedEvaluators[0].status === "SUCCEEDED") {
                    relatedEvaluatorStatus = "evaluated";
                    relatedEvaluatorId = relatedEvaluators[0].id;
                }
            }
            return {
                name: model.name,
                evaluatorStatus: relatedEvaluatorStatus,
                relatedEvaluatorId: relatedEvaluatorId,
                owner: model.created_by?.fullname,
                createdDate: model.created_date && formattedDate,
                model_id: model.external_id,
                source: connection?.name,
                source_type: connection?.type,
                status: model.status,
                type: model.type,
                llm: model
            };
        })
            .reverse();
    }, [type]);
    const [sortedData, updateSortedData] = useState(defaultSortedData);
    const getSortFunction = (sortBy) => {
        switch (sortBy) {
            case "createdDate":
                return getNumericComparator((x) => Date.parse(x[sortBy]));
            case "owner":
            case "name":
            case "status":
            case "source":
            case "evaluatorStatus":
                return getStringComparator((x) => x[sortBy] ?? "");
            default:
                return getNumericComparator(() => 0);
        }
    };
    const sort = ({ sortBy, sortDirection }) => {
        if (!isValidDataKey(sortBy)) {
            return;
        }
        const sortFunc = getSortFunction(sortBy);
        updateSortBy(sortBy);
        updateSortDirection(sortDirection);
        return updateSortedData(defaultSortedData.toSorted(sortDirection === SortDirection.ASC ? sortFunc : reverseOrder(sortFunc)));
    };
    useEffect(() => {
        sort({ sortBy, sortDirection });
    }, [type]);
    const filteredData = sortedData.filter((item) => searchFilter === "" || searchFilter === undefined
        ? item
        : item?.name?.toLowerCase()?.includes(searchFilter?.toLowerCase()));
    const rowHeight = 44;
    const tableHeight = filteredData?.length * rowHeight + 30;
    if (Boolean(searchFilter?.length) && filteredData?.length === 0) {
        return React.createElement("div", { className: styles.noMatchingText }, "No matching models found");
    }
    const selectedAccountId = useSelector((state) => accountsReducer.getSelectedAccountId(state));
    const handleDisableModel = async (modelId) => {
        updateIsLoading(true);
        await dispatch(aiServiceReducer.disableModel(modelId));
        updateIsLoading(false);
    };
    const handleEnableModel = async (modelId) => {
        updateIsLoading(true);
        await dispatch(aiServiceReducer.enableModel(modelId));
        updateIsLoading(false);
    };
    const handleDeleteModel = async (modelId) => {
        updateIsLoading(true);
        await dispatch(aiServiceReducer.handleDeleteModel(selectedAccountId, modelId));
        updateIsLoading(false);
    };
    const columns = type == "CONTENT_GENERATION" ? COLUMNS : COLUMNS.filter((c) => c.dataKey != "evaluatorStatus");
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.dataTableWrapper },
            React.createElement(AutoSizer, null, ({ width }) => (React.createElement(Table, { width: width, height: tableHeight, headerHeight: 30, rowHeight: rowHeight, rowCount: filteredData?.length, rowGetter: ({ index }) => filteredData[index], sort: sort, sortBy: sortBy, sortDirection: sortDirection, rowClassName: styles.fineTuningRow, className: styles.fineTuningTable, headerClassName: styles.fineTuningHeaderRow }, columns.map((column, index) => {
                if (column.dataKey === "name") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, flexGrow: column.flexGrow, className: styles.fineTuningItem, cellRenderer: ({ rowData, cellData }) => (React.createElement("div", { className: styles.fineTuningItem },
                            React.createElement("div", { className: styles.modelLogoNameItem },
                                React.createElement(ModelTypeIcon, { ...rowData }),
                                cellData))) }));
                }
                if (column.dataKey === "source") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, flexGrow: column.flexGrow, className: styles.fineTuningSourceItem }));
                }
                if (column.dataKey === "evaluatorStatus") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, flexGrow: column.flexGrow, className: styles.fineTuningItem, cellRenderer: ({ rowData }) => React.createElement(EvaluatorStatus, { status: rowData.evaluatorStatus }) }));
                }
                if (column.dataKey === "status") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningItem, cellRenderer: ({ rowData }) => {
                            return (React.createElement("div", { className: styles.fineTuningItem },
                                React.createElement(ModelStatus, { status: rowData?.status })));
                        } }));
                }
                if (column.dataKey === "actions") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningSettings, cellRenderer: ({ rowData }) => {
                            if (rowData.type !== "CONTENT_GENERATION" ||
                                (rowData.status !== "ENABLED" &&
                                    rowData.status !== "DISABLED" &&
                                    rowData.status !== "SUCCEEDED")) {
                                return null;
                            }
                            return (React.createElement("div", { className: styles.fineTuningItemActions },
                                React.createElement(Button, { isSmall: true, onClick: (e) => {
                                        e.stopPropagation();
                                        handleModelEvaluationAction(rowData.llm?.version_id);
                                    }, style: { padding: "5px 7px", marginRight: "4px" } }, "Evaluate Model"),
                                React.createElement(Button, { isSmall: true, onClick: (e) => {
                                        e.stopPropagation();
                                        handleFineTuneAction(rowData.exampleSet);
                                    }, style: { padding: "5px 7px", marginRight: "4px" }, disabled: rowData.totalFeedback < FEEDBACK_BREAKPOINTS.MIN }, "Post Train New Model"),
                                rowData.evaluatorStatus === "evaluated" && (React.createElement(Button, { isSmall: true, onClick: (e) => {
                                        e.stopPropagation();
                                        handleEvaluatorAction(rowData.relatedEvaluatorId);
                                    }, style: { padding: "5px 7px", marginRight: "4px" } }, "Evaluator Details"))));
                        } }));
                }
                if (column.dataKey === "more") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningSettings, cellRenderer: ({ rowData }) => {
                            return (React.createElement("div", { className: styles.fineTuningItemMore },
                                React.createElement(DropdownMenu, null,
                                    React.createElement(DropdownMenuTrigger, { asChild: true },
                                        React.createElement(FontAwesomeIcon, { icon: faEllipsisVertical })),
                                    React.createElement(DropdownMenuContent, { className: "w-56", align: "end" },
                                        rowData.status == "ENABLED" && (React.createElement(DropdownMenuItem, { onClick: () => handleDisableModel(rowData.llm?.id), disabled: isLoading }, "Disable Model")),
                                        rowData.status == "DISABLED" && (React.createElement(DropdownMenuItem, { onClick: () => handleEnableModel(rowData.llm?.id), disabled: isLoading }, "Enable Model")),
                                        rowData.llm?.permissions?.can_delete && (React.createElement(DropdownMenuItem, { onClick: () => {
                                                handleDeleteModel(rowData.llm?.id);
                                            }, disabled: isLoading, className: "text-red-500" }, "Delete Model"))))));
                        } }));
                }
                return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.fineTuningItem }));
            })))))));
};
export default ProvisionedModelsTable;
